import { useState } from "react";
import { Delete, Lock } from "lucide-react";
import { verifyPinApi } from "@/lib/api";

export default function PinVerification({ onVerified, onError }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleDigit = (digit) => {
    if (pin.length >= 4) return;
    const newPin = pin + digit;
    setPin(newPin);
    setError("");
    if (newPin.length === 4) {
      submitPin(newPin);
    }
  };

  const handleDelete = () => {
    setPin((p) => p.slice(0, -1));
    setError("");
  };

  const submitPin = async (code) => {
    setLoading(true);
    try {
      const res = await verifyPinApi(code);
      if (res.data.verified) {
        onVerified?.();
      } else {
        setPin("");
        setError("Invalid PIN");
      }
    } catch (e) {
      setPin("");
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Invalid PIN. Try again.";
      setError(msg);
      onError?.(msg);
    }
    setLoading(false);
  };

  const digits = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"];

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-4" data-testid="pin-verification">
      <div className="w-full max-w-sm text-center">
        <div className="mb-8">
          <div className="w-16 h-16 bg-[#1A362D] rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-2xl font-['Playfair_Display'] font-semibold text-[#1C1917]">
            Enter Your PIN
          </h2>
          <p className="text-sm text-[#57534E] mt-1">4-digit security code</p>
        </div>

        {/* PIN Dots */}
        <div className="flex justify-center gap-4 mb-8">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              data-testid={`pin-dot-${i}`}
              className={`w-4 h-4 rounded-full transition-all duration-200 ${
                i < pin.length
                  ? "bg-[#1A362D] scale-110"
                  : "bg-[#E7E5DF]"
              }`}
            />
          ))}
        </div>

        {error && (
          <p className="text-sm text-[#E07A5F] mb-4 animate-fade-in" data-testid="pin-error">
            {error}
          </p>
        )}

        {loading && (
          <div className="flex justify-center mb-4">
            <div className="w-6 h-6 border-2 border-[#1A362D] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3 max-w-[240px] mx-auto">
          {digits.map((d, i) => {
            if (d === "") return <div key={i} />;
            if (d === "del") {
              return (
                <button
                  key={i}
                  data-testid="pin-delete-btn"
                  onClick={handleDelete}
                  className="h-14 rounded-md flex items-center justify-center text-[#78716C] hover:bg-[#F1EFEA] transition-colors"
                >
                  <Delete className="w-5 h-5" />
                </button>
              );
            }
            return (
              <button
                key={i}
                data-testid={`pin-key-${d}`}
                onClick={() => handleDigit(d)}
                disabled={loading}
                className="h-14 rounded-md bg-white border border-[#E7E5DF] text-xl font-['IBM_Plex_Sans'] font-semibold text-[#1C1917] hover:bg-[#F1EFEA] hover:border-[#1A362D] active:bg-[#1A362D] active:text-white transition-all duration-150"
              >
                {d}
              </button>
            );
          })}
        </div>

        <p className="text-xs text-[#78716C] mt-8">
          Secure verification required
        </p>
      </div>
    </div>
  );
}
